import React, { useEffect, useState } from 'react';
import { audioService } from '@/services/audioService';
import { Mic, Volume2 } from 'lucide-react';

interface AudioMeterProps {
  showLabels?: boolean;
  compact?: boolean;
  systemAudioEnabled?: boolean;
}

export const AudioMeter: React.FC<AudioMeterProps> = ({
  showLabels = true,
  compact = false,
  systemAudioEnabled = true
}) => {
  const [micLevel, setMicLevel] = useState<number>(0);
  const [sysLevel, setSysLevel] = useState<number>(0);

  useEffect(() => {
    const unsubscribe = audioService.subscribeLevels((m, s) => {
      setMicLevel(m);
      setSysLevel(s);
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className={`flex flex-col gap-2 ${compact ? 'p-2' : 'p-3 bg-brand-surface/60 rounded-xl border border-brand-border/60'}`}>
      {/* Microphone Bar */}
      <div className="flex items-center gap-2 text-xs">
        {showLabels && (
          <div className="flex items-center gap-1.5 w-24 text-brand-muted shrink-0">
            <Mic className={`w-3.5 h-3.5 ${micLevel > 15 ? 'text-brand-secondary animate-pulse' : 'text-brand-muted'}`} />
            <span className="truncate">Microphone</span>
          </div>
        )}
        <div className="flex-1 h-2 bg-brand-elevated rounded-full overflow-hidden flex items-center p-0.5 border border-brand-border/40">
          <div
            className="h-full rounded-full transition-all duration-75 ease-out"
            style={{
              width: `${Math.min(100, Math.max(3, micLevel))}%`,
              background: micLevel > 80 
                ? 'linear-gradient(90deg, #32D6C5, #EAB308, #FF4D67)' 
                : 'linear-gradient(90deg, #32D6C5, #7C5CFC)'
            }}
          />
        </div>
        <span className="text-[10px] tabular-nums font-mono text-brand-muted w-7 text-right">
          {micLevel > 0 ? `-${Math.max(0, 100 - micLevel)}dB` : '-inf'}
        </span>
      </div>

      {/* System Audio Bar */}
      <div className="flex items-center gap-2 text-xs">
        {showLabels && (
          <div className="flex items-center gap-1.5 w-24 text-brand-muted shrink-0">
            <Volume2 className={`w-3.5 h-3.5 ${sysLevel > 15 ? 'text-brand-accent animate-pulse' : 'text-brand-muted'}`} />
            <span className="truncate">System Audio</span>
          </div>
        )}
        <div className="flex-1 h-2 bg-brand-elevated rounded-full overflow-hidden flex items-center p-0.5 border border-brand-border/40">
          <div
            className="h-full rounded-full transition-all duration-75 ease-out"
            style={{
              width: systemAudioEnabled ? `${Math.min(100, Math.max(3, sysLevel))}%` : '0%',
              background: 'linear-gradient(90deg, #7C5CFC, #32D6C5)'
            }}
          />
        </div>
        <span className="text-[10px] tabular-nums font-mono text-brand-muted w-7 text-right">
          {systemAudioEnabled && sysLevel > 0 ? `-${Math.max(0, 100 - sysLevel)}dB` : (systemAudioEnabled ? '-inf' : 'OFF')}
        </span>
      </div>
    </div>
  );
};
